import React, { useState, useEffect, Component } from 'react';
import { Menu, Sun, Moon, AlertTriangle, Check, Plus, X } from 'lucide-react';
import { supabase } from './lib/supabase';
import { UserDataProvider, useUserData } from './context/UserDataContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Sidebar from './components/Sidebar';
import Landing from './components/Landing';
import Overview from './components/Overview';
import Budget from './components/Budget';
import NetWorth from './components/NetWorth';
import Retirement from './components/Retirement';
import RSUs from './components/RSUs';
import Goals from './components/Goals';
import CreditScore from './components/CreditScore';
import Calendar from './components/Calendar';
import Family from './components/Family';
import Investing from './components/Investing';
import Learn from './components/Learn';
import Settings from './components/Settings';
import Reports from './components/Reports';
import AIAdvisor from './components/AIAdvisor';
import RecurringTransactions from './components/RecurringTransactions';
import DebtPlanner from './components/DebtPlanner';
import OnboardingTutorial from './components/OnboardingTutorial';
import WelcomeSurvey from './components/WelcomeSurvey';
import ChoosePlan from './components/ChoosePlan';
import NotificationPanel, { NotificationBell } from './components/NotificationPanel';
import MoodCheckinModal, { getWeekKey } from './components/MoodCheckin';
import { saveMoodCheckin } from './lib/userData';
import MonthInReviewModal, { getMonthKey, getPrevMonthKey, computePrevMonthStats } from './components/MonthInReviewModal';

// ── Root error boundary — shows error message instead of blank white screen ───
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(err) { return { error: err }; }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-slate-800 border border-rose-700/40 rounded-2xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-rose-400 text-lg font-bold">!</span>
              </div>
              <div>
                <div className="font-bold text-slate-100">Something went wrong</div>
                <div className="text-xs text-slate-400 mt-0.5">Finovo encountered an error</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 font-mono bg-slate-900 rounded-lg p-3 break-all">
              {this.state.error?.message || 'Unknown error'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const TABS = [
  { id: 'overview',   label: 'Overview' },
  { id: 'budget',     label: 'Budget' },
  { id: 'networth',   label: 'Net Worth' },
  { id: 'investing',  label: 'Investing' },
  { id: 'retirement', label: 'Retirement' },
  { id: 'rsus',       label: 'RSUs' },
  { id: 'goals',      label: 'Goals' },
  { id: 'credit',     label: 'Credit Score' },
  { id: 'calendar',   label: 'Calendar' },
  { id: 'family',     label: 'Family' },
  { id: 'recurring',  label: 'Recurring' },
  { id: 'debt',       label: 'Debt Planner' },
  { id: 'ai',         label: 'AI Advisor' },
  { id: 'learn',      label: 'Learn' },
  { id: 'reports',    label: 'Reports' },
  { id: 'settings',   label: 'Settings' },
];

// ── localStorage helpers (per-user, survives Supabase save failures) ──────────
// Keys are scoped by userId so multi-account sign-in on the same browser works.
function readLS(key)      { try { return localStorage.getItem(key) === '1'; } catch { return false; } }
function writeLS(key)     { try { localStorage.setItem(key, '1');           } catch { /* ignore */ } }

// Tab persistence helpers — save/restore the last visited top-level tab.
function readLastTab(uid)        { try { return localStorage.getItem(`finovo_tab_${uid}`) || null; } catch { return null; } }
function writeLastTab(uid, tab)  { try { localStorage.setItem(`finovo_tab_${uid}`, tab);            } catch { /* ignore */ } }

// ── QuickAddModal — global floating expense logger ────────────────────────────

function QuickAddModal({ onClose, onSave, categories }) {
  const today = new Date().toISOString().split('T')[0];
  const [amount,      setAmount]      = useState('');
  const [description, setDescription] = useState('');
  const [category,    setCategory]    = useState(categories[0]?.name || '');
  const [date,        setDate]        = useState(today);
  const [saved,       setSaved]       = useState(false);

  const fmt = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;
    onSave({
      id: `txn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      date,
      amount: amt,
      description: description.trim() || `${category} expense`,
      category: category || null,
      type: null,
      kind: 'expense',
      source: 'manual',
      reviewed: !!category,
    });
    setSaved(true);
    setTimeout(onClose, 900);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h2 className="font-semibold text-slate-100 text-base">Quick Add Expense</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors">
            <X size={16} />
          </button>
        </div>

        {saved ? (
          <div className="px-5 pb-6 pt-3 flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center">
              <Check size={22} className="text-white" />
            </div>
            <p className="text-slate-200 font-medium">Expense logged!</p>
            <p className="text-slate-400 text-sm">{fmt(amount)} added to {category || 'your budget'}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-5 pb-5 flex flex-col gap-4">
            {/* Amount — large, auto-focus */}
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Amount</label>
              <div className="flex items-center bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 gap-2 focus-within:border-blue-500 transition-colors">
                <span className="text-slate-400 font-medium">$</span>
                <input
                  autoFocus
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  className="flex-1 bg-transparent text-slate-100 text-lg font-semibold focus:outline-none tabular-nums placeholder:text-slate-600"
                  required
                />
              </div>
            </div>

            {/* Category + Date in a row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Category</label>
                <select
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  className="w-full bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                >
                  <option value="">-- None --</option>
                  {categories.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wider mb-1.5 block">Description <span className="text-slate-600 normal-case">(optional)</span></label>
              <input
                type="text"
                placeholder="e.g. Coffee with Maya"
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full bg-slate-700/60 border border-slate-600 rounded-xl px-3 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-blue-500 transition-colors placeholder:text-slate-600"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!amount || parseFloat(amount) <= 0}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold rounded-xl transition-colors text-sm"
            >
              Log Expense
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Inner Dashboard (has access to UserDataContext + ThemeContext) ────────────

function Dashboard({ session, handleSignOut, activeTab, setActiveTab }) {
  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickAdd,   setShowQuickAdd]   = useState(false);

  const {
    userProfile, updateSection,
    budgetData, netWorthData, goalsData, retirementData, calendarData,
    dataLoading, saveError, saveSuccess, connectionError,
  } = useUserData();

  // ── Quick-add expense handler — writes directly to budget transactions ────
  const handleQuickSave = (txn) => {
    const current = budgetData || {};
    const transactions = Array.isArray(current.transactions) ? current.transactions : [];
    updateSection('budget', { ...current, transactions: [txn, ...transactions] });
  };

  const { theme, toggleTheme } = useTheme();

  // ── Onboarding / survey completion flags ───────────────────────────────────
  // Source of truth: Supabase flag (persists across devices).
  // Fallback:        per-user localStorage entry (survives Supabase save failures
  //                  and optimistic-update rollbacks within the same browser).
  const uid         = session?.user?.id ?? null;
  const lsOnboard   = uid ? `finovo_onboard_${uid}` : null;
  const lsSurvey    = uid ? `finovo_survey_${uid}`  : null;

  const onboardingComplete = (userProfile?.onboardingComplete ?? false) || readLS(lsOnboard);
  const surveyComplete     = (userProfile?.surveyComplete     ?? false) || readLS(lsSurvey);

  // ── Onboarding / survey show state ────────────────────────────────────────
  const [showOnboarding,   setShowOnboarding]   = useState(false);
  const [showSurvey,       setShowSurvey]       = useState(false);
  const [showChoosePlan,   setShowChoosePlan]   = useState(false);
  // Session guard — once plan screen has been shown this session, never re-trigger.
  const [planScreenShownThisSession, setPlanScreenShownThisSession] = useState(false);
  const [showMoodCheckin,  setShowMoodCheckin]  = useState(false);
  // Session guard — don't show if user has already dismissed it in this session
  const [moodCheckinShownThisSession, setMoodCheckinShownThisSession] = useState(false);
  const [showMIR,          setShowMIR]          = useState(false);
  const [mirStats,         setMirStats]         = useState(null);
  const [mirShownThisSession, setMirShownThisSession] = useState(false);
  // Session-guard: prevents double-showing within the same page load.
  // Cross-session suppression: if localStorage already has the flag we seed this
  // to `true` synchronously (before first render) so the effect below never fires
  // for returning users — no flash of the tutorial on refresh or re-login.
  const [onboardingShownThisSession, setOnboardingShownThisSession] = useState(
    () => !!(lsOnboard && readLS(lsOnboard))
  );

  useEffect(() => {
    // Show onboarding when ALL three conditions hold:
    //  1. Data has fully loaded from Supabase (!dataLoading)         ← prevents flash during load
    //  2. The flag is false in BOTH Supabase AND localStorage        ← genuine new user
    //  3. We haven't already shown it in this browser session        ← prevents double-show
    // NOTE: isNewUser is intentionally NOT part of this condition — the Supabase
    // flag is the sole source of truth regardless of whether financial data exists.
    // NOTE: No setTimeout — the structural guard above (dataLoading) is the right
    // mechanism. A delay here would only mask the real gating and add latency for new users.
    if (!dataLoading && !onboardingComplete && !onboardingShownThisSession) {
      setShowOnboarding(true);
      setOnboardingShownThisSession(true);
    }
  }, [dataLoading, onboardingComplete, onboardingShownThisSession]);

  // ── Mood check-in — show once per ISO week on Monday (or first open of the week) ──
  // localStorage key: `finovo_mood_week_${uid}` stores the last week key when prompted.
  // Never shows if onboarding or survey is still pending.
  useEffect(() => {
    if (dataLoading) return;
    if (!onboardingComplete || !surveyComplete) return;
    if (showOnboarding || showSurvey) return;
    if (moodCheckinShownThisSession) return;
    const lsMoodWeek = uid ? `finovo_mood_week_${uid}` : null;
    if (!lsMoodWeek) return;
    const currentWeek = getWeekKey();
    let lastWeek;
    try { lastWeek = localStorage.getItem(lsMoodWeek); } catch { lastWeek = null; }
    if (lastWeek !== currentWeek) {
      // Slight delay so the page settles before showing the modal
      const t = setTimeout(() => {
        setShowMoodCheckin(true);
        setMoodCheckinShownThisSession(true);
      }, 1800);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, onboardingComplete, surveyComplete, showOnboarding, showSurvey, uid]);

  const handleMoodCheckinComplete = (mood) => {
    const currentWeek = getWeekKey();
    const lsMoodWeek  = uid ? `finovo_mood_week_${uid}` : null;
    if (lsMoodWeek) {
      try { localStorage.setItem(lsMoodWeek, currentWeek); } catch {}
    }
    if (uid) saveMoodCheckin(uid, { mood, weekKey: currentWeek });
  };

  const handleMoodCheckinClose = () => {
    // Mark this week as seen even if user skipped, so it doesn't re-appear
    const currentWeek = getWeekKey();
    const lsMoodWeek  = uid ? `finovo_mood_week_${uid}` : null;
    if (lsMoodWeek) {
      try { localStorage.setItem(lsMoodWeek, currentWeek); } catch {}
    }
    setShowMoodCheckin(false);
  };

  // ── Month in Review — show once per month, reviewing the previous month ──────
  // localStorage key: `finovo_mir_month_${uid}` = last reviewed month key 'YYYY-MM'.
  // Shows only after onboarding + survey complete, never at same time as mood check-in.
  useEffect(() => {
    if (dataLoading) return;
    if (!onboardingComplete || !surveyComplete) return;
    if (showOnboarding || showSurvey) return;
    if (mirShownThisSession) return;
    const lsMirMonth = uid ? `finovo_mir_month_${uid}` : null;
    if (!lsMirMonth) return;

    const prevMonth   = getPrevMonthKey();
    const currentMonth = getMonthKey();
    let lastReviewed;
    try { lastReviewed = localStorage.getItem(lsMirMonth); } catch { lastReviewed = null; }

    // Show if we haven't reviewed the previous month yet (and it's not the very first month)
    if (lastReviewed !== prevMonth && lastReviewed !== currentMonth) {
      const stats = computePrevMonthStats(budgetData, prevMonth);
      // Only show if there's some data to display (at least 1 transaction or bill)
      if (stats.txnCount > 0 || stats.billsTotal > 0) {
        const t = setTimeout(() => {
          setMirStats(stats);
          setShowMIR(true);
          setMirShownThisSession(true);
        }, 2400);
        return () => clearTimeout(t);
      } else {
        // Mark as seen so it doesn't re-check on every render when no data
        try { localStorage.setItem(lsMirMonth, prevMonth); } catch {}
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, onboardingComplete, surveyComplete, showOnboarding, showSurvey, uid, budgetData]);

  const handleMIRClose = () => {
    const lsMirMonth = uid ? `finovo_mir_month_${uid}` : null;
    if (lsMirMonth) {
      try { localStorage.setItem(lsMirMonth, getPrevMonthKey()); } catch {}
    }
    setShowMIR(false);
  };

  // ── Plan selection — show once after onboarding+survey, skip if plan already set ──
  // Source of truth: userProfile.plan in Supabase (null = not chosen yet).
  // Never shows if onboarding or survey is still pending.
  const planChosen = !!(userProfile?.plan);
  useEffect(() => {
    if (dataLoading) return;
    if (!onboardingComplete || !surveyComplete) return;
    if (planChosen) return;
    if (showOnboarding || showSurvey) return;
    if (planScreenShownThisSession) return;
    setShowChoosePlan(true);
    setPlanScreenShownThisSession(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoading, onboardingComplete, surveyComplete, planChosen, showOnboarding, showSurvey]);

  const handleSelectFree = () => {
    setShowChoosePlan(false);
    updateSection('user_profile', { ...userProfile, plan: 'free' });
  };

  const handleJoinWaitlist = () => {
    // Save plan + waitlist flag. Route to dashboard after 2s (ChoosePlan shows confirmation).
    updateSection('user_profile', { ...userProfile, plan: 'free', waitlist: true });
    setTimeout(() => setShowChoosePlan(false), 2000);
  };

  // Write localStorage FIRST (sync, never rolls back), THEN persist to Supabase.
  // This order ensures the flag survives even if the async Supabase save fails
  // and updateSection rolls back the optimistic state update.
  const handleOnboardingComplete = () => {
    writeLS(lsOnboard);
    setShowOnboarding(false);
    updateSection('user_profile', { ...userProfile, onboardingComplete: true });
    if (!surveyComplete) setTimeout(() => setShowSurvey(true), 300);
  };

  const handleOnboardingSkip = () => {
    writeLS(lsOnboard);
    setShowOnboarding(false);
    updateSection('user_profile', { ...userProfile, onboardingComplete: true });
    if (!surveyComplete) setTimeout(() => setShowSurvey(true), 300);
  };

  const handleSurveyComplete = (answers) => {
    writeLS(lsOnboard); // defensive: ensure both flags are always set
    writeLS(lsSurvey);
    setShowSurvey(false);
    updateSection('user_profile', {
      ...userProfile,
      onboardingComplete: true,   // always include both flags together
      surveyComplete: true,
      surveyAnswers: answers,
      riskTolerance: answers.riskTolerance || userProfile?.riskTolerance || 'moderate',
    });
  };

  const handleSurveySkip = () => {
    writeLS(lsOnboard); // defensive: ensure both flags are always set
    writeLS(lsSurvey);
    setShowSurvey(false);
    updateSection('user_profile', {
      ...userProfile,
      onboardingComplete: true,   // was missing — caused fragile stale-closure risk
      surveyComplete: true,
    });
  };

  // ── Content router ─────────────────────────────────────────────────────────
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':    return <Overview setActiveTab={setActiveTab} />;
      case 'budget':      return <Budget />;
      case 'networth':    return <NetWorth />;
      case 'retirement':  return <Retirement />;
      case 'investing':   return <Investing />;
      case 'rsus':        return <RSUs />;
      case 'goals':       return <Goals />;
      case 'credit':      return <CreditScore />;
      case 'calendar':    return <Calendar />;
      case 'family':      return <Family />;
      case 'learn':       return <Learn />;
      case 'reports':     return <Reports />;
      case 'ai':          return <AIAdvisor />;
      case 'recurring':   return <RecurringTransactions />;
      case 'debt':        return <DebtPlanner />;
      case 'settings':    return <Settings onSignOut={handleSignOut} supabaseUser={session?.user} />;
      default:            return <Overview setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="flex h-screen bg-slate-900 text-slate-100 overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={TABS}
        onSignOut={handleSignOut}
        user={session?.user}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-xs">F</span>
            </div>
            <span className="font-bold text-slate-100 text-sm">Finovo</span>
          </div>
          <div className="flex items-center gap-1">
            {/* Theme toggle (mobile) */}
            <button
              onClick={() => {
                toggleTheme();
                updateSection('user_profile', {
                  ...userProfile,
                  theme: theme === 'dark' ? 'light' : 'dark',
                }, { silent: true });
              }}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
              title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <NotificationBell
              onClick={() => setShowNotifications(v => !v)}
              budgetData={budgetData}
              netWorthData={netWorthData}
              goalsData={goalsData}
              retirementData={retirementData}
              calendarData={calendarData}
              userProfile={userProfile}
            />
          </div>
        </header>

        {/* Desktop: theme toggle + notification bell — fixed top-right */}
        <div className="hidden lg:flex items-center gap-1 fixed top-4 right-6 z-40">
          <button
            onClick={() => {
              toggleTheme();
              updateSection('user_profile', {
                ...userProfile,
                theme: theme === 'dark' ? 'light' : 'dark',
              }, { silent: true });
            }}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <NotificationBell
            onClick={() => setShowNotifications(v => !v)}
            budgetData={budgetData}
            netWorthData={netWorthData}
            goalsData={goalsData}
            retirementData={retirementData}
            calendarData={calendarData}
            userProfile={userProfile}
          />
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          {renderContent()}
        </main>

        {/* Floating Quick-Add button */}
        <button
          onClick={() => setShowQuickAdd(true)}
          title="Quick add expense"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white rounded-full shadow-2xl flex items-center justify-center transition-all"
        >
          <Plus size={26} strokeWidth={2.5} />
        </button>
      </div>

      {/* Overlays */}
      {showQuickAdd && (
        <QuickAddModal
          onClose={() => setShowQuickAdd(false)}
          onSave={(txn) => { handleQuickSave(txn); }}
          categories={Array.isArray(budgetData?.categories) ? budgetData.categories : []}
        />
      )}
      {showNotifications && (
        <NotificationPanel
          onClose={() => setShowNotifications(false)}
          setActiveTab={(tab) => { setActiveTab(tab); setShowNotifications(false); }}
        />
      )}
      {/* !dataLoading guard: never render while Supabase fetch is in-flight.
          Combined with the lazy-init of onboardingShownThisSession above, this
          eliminates every possible flash path for returning users. */}
      {showOnboarding && !dataLoading && (
        <OnboardingTutorial
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          setActiveTab={setActiveTab}
        />
      )}
      {showSurvey && !dataLoading && (
        <WelcomeSurvey
          onComplete={handleSurveyComplete}
          onSkip={handleSurveySkip}
        />
      )}
      {showChoosePlan && !dataLoading && !showOnboarding && !showSurvey && (
        <ChoosePlan
          onSelectFree={handleSelectFree}
          onJoinWaitlist={handleJoinWaitlist}
        />
      )}
      {showMoodCheckin && !dataLoading && !showOnboarding && !showSurvey && (
        <MoodCheckinModal
          onClose={handleMoodCheckinClose}
          onComplete={handleMoodCheckinComplete}
          budgetData={budgetData}
          goalsData={goalsData}
          transactions={Array.isArray(budgetData?.transactions) ? budgetData.transactions : []}
        />
      )}
      {showMIR && !dataLoading && !showOnboarding && !showSurvey && !showMoodCheckin && (
        <MonthInReviewModal
          stats={mirStats}
          onClose={handleMIRClose}
        />
      )}

      {/* ── Database connection error banner (table not found / RLS misconfigured) ── */}
      {connectionError && (
        <div className="fixed top-0 left-0 right-0 z-[70] flex items-center justify-center gap-2 px-4 py-2 bg-amber-950/95 border-b border-amber-700/60 text-xs text-amber-200 shadow-lg backdrop-blur">
          <AlertTriangle size={13} className="flex-shrink-0 text-amber-400" />
          <span>
            Database connection issue — data may not be saved.&nbsp;
            <span className="text-amber-400">Run the Supabase setup SQL if this is a fresh install.</span>
          </span>
        </div>
      )}

      {/* ── Saved confirmation toast ─────────────────────────────────────────────── */}
      {saveSuccess && !saveError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 px-4 py-2.5 bg-emerald-950/95 border border-emerald-700/60 rounded-xl text-xs text-emerald-200 shadow-2xl backdrop-blur whitespace-nowrap animate-in fade-in slide-in-from-bottom-2 duration-200">
          <Check size={13} className="flex-shrink-0 text-emerald-400" />
          <span>Saved</span>
        </div>
      )}

      {/* ── Save-error toast (individual section save failed) ────────────────────── */}
      {saveError && !connectionError && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 px-4 py-2.5 bg-rose-950/95 border border-rose-700/60 rounded-xl text-xs text-rose-200 shadow-2xl backdrop-blur whitespace-nowrap">
          <AlertTriangle size={13} className="flex-shrink-0 text-rose-400" />
          <span>Changes could not be saved — check your connection.</span>
        </div>
      )}
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────

export default function App() {
  const [session,      setSession]      = useState(null);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [activeTab,    setActiveTab]    = useState('overview');
  // tabRestored: guards the write effect from firing before the initial restoration.
  //
  // WHY THIS EXISTS — race condition:
  //   1. Supabase fires onAuthStateChange(INITIAL_SESSION) which sets session.user.id.
  //   2. The write-effect deps [activeTab, session?.user?.id] notice the new uid and fire.
  //   3. At that moment activeTab is still 'overview' → writeLastTab overwrites the real saved tab.
  //   4. getSession().then() resolves, reads localStorage, finds 'overview' → no restore happens.
  //
  // Fix: only allow writes once getSession().then() has finished reading + applying the saved tab.
  const [tabRestored,  setTabRestored]  = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Resolve the saved tab BEFORE committing anything to state so the write
      // effect never fires with the pre-restoration 'overview' value.
      let restoredTab = null;
      if (session?.user?.id) {
        const saved = readLastTab(session.user.id);
        if (saved && TABS.some(t => t.id === saved)) {
          restoredTab = saved;
        }
      }

      // Batch all state updates into one render so the write effect sees the
      // already-restored activeTab from the very first time it runs.
      setSession(session);
      setAuthLoading(false);
      if (restoredTab) setActiveTab(restoredTab);
      setTabRestored(true);   // ← unlocks the write effect
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Persist the active tab to localStorage whenever the user navigates.
  // Guarded by tabRestored so it never fires before the initial restoration
  // has already been read and applied (see race-condition note above).
  useEffect(() => {
    if (!session?.user?.id || !tabRestored) return;
    writeLastTab(session.user.id, activeTab);
  }, [activeTab, session?.user?.id, tabRestored]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setTabRestored(false);
    setActiveTab('overview');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/50">
            <span className="text-white font-bold text-xl">F</span>
          </div>
          <div className="flex gap-1.5">
            {[0, 1, 2].map(i => (
              <div
                key={i}
                className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!session) return <Landing />;

  return (
    <ErrorBoundary>
      <ThemeProvider initialTheme="dark">
        <UserDataProvider userId={session.user.id} supabaseUser={session.user}>
          <DashboardWithThemeSync
            session={session}
            handleSignOut={handleSignOut}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </UserDataProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Syncs persisted theme from Supabase profile into ThemeContext on first load
function DashboardWithThemeSync(props) {
  const { userProfile, dataLoading } = useUserData();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (!dataLoading && userProfile?.theme && userProfile.theme !== theme) {
      toggleTheme(); // flip to match persisted preference
    }
  }, [dataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Dashboard {...props} />;
}
